import { supabase } from "../lib/supabase";

// The QuickBooks connector's exception queue (DEV-2442). Support reads the
// qbo_connector.open_exceptions view (open rows plus allowed_actions, the
// database's action policy; support-only under RLS, migration 097) and acts on a row through the qbo-exceptions Edge Function, which calls
// the service_role-only RPCs of migration 097.

const COLUMNS =
  "id, stripe_object_type, stripe_object_id, posting_kind, reason_code, reason_detail, context, created_at, allowed_actions";

export const GENERIC_ACTION_ERROR = "Could not update the exception. Try again.";

function client() {
  if (!supabase) {
    throw new Error("Supabase client is not initialized");
  }
  return supabase;
}

// The page reads at most this many rows; total says how many are open.
export const PAGE_LIMIT = 100;

/**
 * The newest open exceptions (at most PAGE_LIMIT) with the actions each
 * allows, and the exact number open. Shape: { rows, total }
 */
export async function fetchOpenExceptions() {
  const { data, error, count } = await client()
    .schema("qbo_connector")
    .from("open_exceptions")
    .select(COLUMNS, { count: "exact" })
    .order("created_at", { ascending: false })
    .limit(PAGE_LIMIT);
  if (error) {
    throw new Error("Could not load the QuickBooks exceptions.");
  }
  const rows = data ?? [];
  return { rows, total: count ?? rows.length };
}

/** How many exceptions are open, without reading them (the QuickBooks page's tab count). */
export async function countOpenExceptions() {
  const { error, count } = await client()
    .schema("qbo_connector")
    .from("open_exceptions")
    .select("id", { count: "exact", head: true });
  if (error) {
    throw new Error("Could not count the QuickBooks exceptions.");
  }
  return count ?? 0;
}

/**
 * Runs one support action ("resolve" | "ignore" | "retry" | "choose_customer")
 * on an exception. A refusal throws an Error whose message is the function's
 * plain sentence and whose `code` is its outcome (already_handled, in_flight ...).
 */
export async function actOnException(action, exceptionId, extra = {}) {
  const { data, error } = await client().functions.invoke("qbo-exceptions", {
    body: { action, exception_id: exceptionId, ...extra },
  });
  if (error) {
    let body = null;
    try {
      if (error.context?.json) body = await error.context.clone().json();
    } catch {
      // fall through to the generic message below
    }
    const err = new Error(body?.message ?? GENERIC_ACTION_ERROR);
    err.code = body?.error ?? null;
    throw err;
  }
  return data;
}
